import { useState, useEffect, useMemo } from "react";
import { BiPlus } from "react-icons/bi";
import { GrAscend, GrDescend } from "react-icons/gr";
import { PiMagnifyingGlass } from "react-icons/pi";
import { VscOrganization } from "react-icons/vsc";
import NewMemberModal from "./NewMemberModal";
import TableRowsLoader from "../Loaders/TableRowsLoader";
import { TbReload } from "react-icons/tb";
import MemberTable from "./MembersTable";
import UpdateMemberModal from "./UpdateMemberModal";
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const MainMembers = ({
    account_id,
    userData,
}) => {
    const {t} = useLanguage()
    const [openModal, setOpenModal] = useState(false);
    const [openModalUpdate, setOpenModalUpdate] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [data, setDatata] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMember, setSelectedMember] = useState({})
    const [orgData, setOrgData] = useState([])
    const [loadingOrgData, setLoadingOrgData] = useState(false)

    const fetchData = async () => {
        if (!account_id) return;
        setLoading(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_members`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ account_id: account_id }),
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || "Failed to fetch");

            setDatata(result.data || []);
        } catch (err) {
            console.error("Error fetching members:", err);
        } finally {
            setTimeout(() => setLoading(false), 500);
        }
    };

    const fetchOrgData = async (abilityId) => {
        setLoadingOrgData(true);

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_organizations_members`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch orgs");
            }

            setOrgData(result.data || []);
        } catch (err) {
            console.error("Error fetching organizations:", err);
        } finally {
            setLoadingOrgData(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [account_id]);

    const filteredAndSortedData = useMemo(() => {
        if (!data) return [];

        let result = data.filter((member) => {
            const term = searchTerm.toLowerCase();
            const nameMatch = `${member.name} ${member.surname}`.toLowerCase().includes(term);
            const emailMatch = member.email?.toLowerCase().includes(term);
            const statusMatch = member.status?.toLowerCase().includes(term);

            const orgMatch = member.organizations?.some(org =>
                org.name?.toLowerCase().includes(term)
            );

            return nameMatch || emailMatch || orgMatch || statusMatch;
        });

        result.sort((a, b) => {
            const nameA = a.name?.toLowerCase() || "";
            const nameB = b.name?.toLowerCase() || "";
            return sortOrder === "asc"
                ? nameA.localeCompare(nameB)
                : nameB.localeCompare(nameA);
        });

        return result;
    }, [data, searchTerm, sortOrder]);

    const handleOpenModalUpdate = (item) => {
        setSelectedMember(item)
        setOpenModalUpdate(true)
    }

    if (loading) return <TableRowsLoader />;

    return (
        <div className="mainframe_item">
            <div className="mainframe_item_header">
                <p className="mainframe_item_title fade-in">{t.members.title}</p>
                <div className="mainframe_item_subtitle fade-in">
                    {data.length === 0 ? (
                        <div className="empty_org_mex_cont">
                            <p>{t.members.empty_subtitle}</p>
                            <div className="empty_org_img">
                                <p className="empty_org_img_title">
                                    <i><VscOrganization /></i> {t.members.quest_title}
                                </p>
                                <p className="empty_org_img_subtitle">
                                    {t.members.quest_answ}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <p>{t.members.subtitle}</p>
                    )}
                </div>
                <button
                    className={`mainframe_item_create_btn fade-in ${data.length === 0 && "mainframe_item_create_btn_anim"}`}
                    onClick={() => setOpenModal(true)}
                >
                    <BiPlus /> {t.create}
                </button>
            </div>

            {(data.length > 0 || searchTerm) && (
                <div className="filter_body_mainframe">
                    <div className="filtering_mainframe">
                        <div className="filtering_mainframe_btn_cont fade-in">
                            {data.length > 1 && (
                                <>
                                    <button className={`filter_btn ${sortOrder === "asc" ? "active" : ""}`} onClick={() => setSortOrder("asc")} title="A-Z">
                                        <GrAscend />
                                    </button>
                                    <button className={`filter_btn ${sortOrder === "desc" ? "active" : ""}`} onClick={() => setSortOrder("desc")} title="Z-A">
                                        <GrDescend />
                                    </button>
                                </>
                            )}
                            <button className="filter_btn" onClick={() => fetchData()} title={t.reload}>
                                <TbReload />
                            </button>
                        </div>
                        <div className="filter_input_cont fade-in">
                            <input
                                className="filter_input"
                                placeholder={t.search_filter}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <span className="filter_input_icon"><PiMagnifyingGlass /></span>
                        </div>
                    </div>

                    <MemberTable
                        type="members"
                        data={filteredAndSortedData}
                        handleOpenModalUpdate={handleOpenModalUpdate}
                        userData={userData}
                    />
                </div>
            )}

            <NewMemberModal
                account_id={account_id}
                show={openModal}
                handleClose={() => setOpenModal(false)}
                fetchRecords={fetchData}
                userData={userData}
                orgData={orgData}
                fetchOrgData={fetchOrgData}
            />

            <UpdateMemberModal
                account_id={account_id}
                show={openModalUpdate}
                handleClose={() => setOpenModalUpdate(false)}
                fetchRecords={fetchData}
                selectedMember={selectedMember}
                userData={userData}
                orgData={orgData}
                fetchOrgData={fetchOrgData}
            />

        </div>
    );
};

export default MainMembers;